import React from 'react'

import {loadMatrixSession} from '#/lib/storage/matrix'
import {useSession} from '#/state/session'

export type ActivationStatus =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'error'

export type ActivationState = {
  social: {status: ActivationStatus; error?: string}
  chat: {status: ActivationStatus; error?: string}
}

type ActivationActions = {
  setSocialStatus: (status: ActivationStatus, error?: string) => void
  setChatStatus: (status: ActivationStatus, error?: string) => void
  resetErrors: () => void
  refreshChatStatus: () => void
}

const initialState: ActivationState = {
  social: {status: 'disconnected'},
  chat: {status: 'disconnected'},
}

const StateContext = React.createContext<ActivationState>(initialState)
const ActionsContext = React.createContext<ActivationActions>({
  setSocialStatus: () => {},
  setChatStatus: () => {},
  resetErrors: () => {},
  refreshChatStatus: () => {},
})

export function Provider({children}: React.PropsWithChildren<{}>) {
  const {hasSession} = useSession()
  const [state, setState] = React.useState<ActivationState>(initialState)

  // Social activation
  React.useEffect(() => {
    setState(prev => ({
      ...prev,
      social: {
        status: hasSession ? 'connected' : 'disconnected',
        error: undefined,
      },
    }))
  }, [hasSession])

  const refreshChatStatus = React.useCallback(() => {
    let cancelled = false
    ;(async () => {
      try {
        const session = await loadMatrixSession()
        if (cancelled) return
        setState(prev => ({
          ...prev,
          chat: {
            status: session?.accessToken ? 'connected' : 'disconnected',
            error: undefined,
          },
        }))
      } catch {
        if (cancelled) return
        setState(prev => ({
          ...prev,
          chat: {status: 'disconnected', error: undefined},
        }))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  // Chat activation (Matrix): refresh on mount + focus + visibility + short polling.
  React.useEffect(() => {
    let cleanupRefresh: undefined | (() => void)
    const run = () => {
      try {
        cleanupRefresh?.()
      } catch {}
      cleanupRefresh = refreshChatStatus() as any
    }

    run()

    // same-tab localStorage writes do NOT trigger "storage" event, so we also do a short poll.
    let tries = 0
    const iv = setInterval(() => {
      tries += 1
      run()
      if (tries >= 12) clearInterval(iv) // ~6s
    }, 500)

    const onFocus = () => run()
    const onVis = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        run()
      }
    }
    const onStorage = (e: any) => {
      const k = e?.key
      if (k === 'matrix:session:v1' || k === 'sparker_matrix_session') {
        run()
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('focus', onFocus)
      window.addEventListener('storage', onStorage)
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVis)
    }

    return () => {
      clearInterval(iv)
      try {
        cleanupRefresh?.()
      } catch {}
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', onFocus)
        window.removeEventListener('storage', onStorage)
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVis)
      }
    }
  }, [refreshChatStatus])

  const setSocialStatus = React.useCallback(
    (status: ActivationStatus, error?: string) => {
      setState(prev => ({
        ...prev,
        social: {status, error},
      }))
    },
    [],
  )

  const setChatStatus = React.useCallback(
    (status: ActivationStatus, error?: string) => {
      setState(prev => ({
        ...prev,
        chat: {status, error},
      }))
    },
    [],
  )

  const resetErrors = React.useCallback(() => {
    setState(prev => ({
      social: {...prev.social, error: undefined},
      chat: {...prev.chat, error: undefined},
    }))
  }, [])

  return (
    <StateContext.Provider value={state}>
      <ActionsContext.Provider
        value={{setSocialStatus, setChatStatus, resetErrors, refreshChatStatus}}>
        {children}
      </ActionsContext.Provider>
    </StateContext.Provider>
  )
}

export function useActivationState() {
  return React.useContext(StateContext)
}

export function useActivationActions() {
  return React.useContext(ActionsContext)
}

export function useIsActivationComplete() {
  const {chat} = useActivationState()
  return chat.status === 'connected'
}
